"use client";

import React, { useState, useEffect, useRef } from 'react';
import { usePathname } from "next/navigation";
import { PROJECTS_DELIVERED, HAPPY_CUSTOMERS, STATS_UPDATED_AT } from "@/lib/homepageStats";
import StatNumber from "@/components/StatNumber";
import UpdatedAt from "@/components/UpdatedAt";

const StatsSection: React.FC = () => {
  const [years, setYears] = useState(0);
  const [customers, setCustomers] = useState(0);
  const [projects, setProjects] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const pathname = usePathname();
const isFintech =
  pathname === "/fintech" || pathname === "/custom-software";

  useEffect(() => {
    // prefers-reduced-motion: land on the real figures, skip the count-up entirely.
    // Same check as hooks/useAOS.ts.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      setYears(10);
      setCustomers(HAPPY_CUSTOMERS);
      setProjects(PROJECTS_DELIVERED);
      setHasAnimated(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);

          const duration = 2000;
          const steps = 50;
          const yearsTarget = 10;
          const customersTarget = HAPPY_CUSTOMERS;
          const projectsTarget = PROJECTS_DELIVERED;

          const yearsStep = yearsTarget / steps;
          const customersStep = customersTarget / steps;
          const projectsStep = projectsTarget / steps;
          const intervalTime = duration / steps;

          let currentStep = 0;

          const interval = setInterval(() => {
            if (currentStep >= steps) {
              clearInterval(interval);
              setYears(yearsTarget);
              setCustomers(customersTarget);
              setProjects(projectsTarget);
              return;
            }

            setYears((prev) => Math.min(prev + yearsStep, yearsTarget));
            setCustomers((prev) => Math.min(prev + customersStep, customersTarget));
            setProjects((prev) => Math.min(prev + projectsStep, projectsTarget));
            currentStep++;
          }, intervalTime);
        }
      },
      // Was 0.5. A stats block taller than half the viewport can never reach that ratio,
      // so on smaller screens the figures stayed on 0 for the whole visit.
      { threshold: 0 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    // Backstop: if the observer never fires, show the real figures rather than zeros.
    const failsafe = window.setTimeout(() => {
      setHasAnimated((already) => {
        if (already) return already;
        setYears(10);
        setCustomers(HAPPY_CUSTOMERS);
        setProjects(PROJECTS_DELIVERED);
        return true;
      });
    }, 4000);

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated]);

  return (
    <section ref={sectionRef} className="">
      <div className="max-w-5xl px-4 mx-auto sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 gap-0 sm:gap-8 mt-10 text-center sm:gap-x-8 lg:grid-cols-3">
          <div className="border-b border-[#DADADA] py-6 pb-9  sm:border-b flex justify-center flex-col items-center md:border-b-0 md:border-r md:border-r-[#DADADA] ">
            <div className='flex items-start flex-col justify-center'>
            <div className="p-0 m-0 font-bold mb-1 ">
              <span className="text-[#5081FF] text-[40px] font-bold  font-NeueMontreal text-start">
                {Math.floor(years)}+
              </span>
            </div>
            <p  data-aos="zoom-in" className={`p-0 m-0 text-[18px] font-medium leading-[100%] schibsted-grotesk tracking-wide ${isFintech ? "text-white" : "text-[#fff]"}`}>
              Years in business
            </p>
            
          </div>
</div>
          <div  className="border-b border-[#DADADA] py-6 pb-9 sm:border-b flex justify-center flex-col items-center md:border-b-0 md:border-r md:border-r-[#DADADA]">
           <div className='flex items-start flex-col justify-center'>
            <div className="p-0 m-0 font-bold mb-1 ">
              <span className="text-[#5081FF] text-[40px] font-bold leading-[150%] font-NeueMontreal text-start">
                <StatNumber display={`${Math.floor(customers)}+`} exact={HAPPY_CUSTOMERS} />
              </span>
            </div>
            <p data-aos="zoom-in" className={`p-0 m-0 text-[18px] font-medium leading-[100%] schibsted-grotesk tracking-wide ${isFintech ? "text-white" : "text-[#fff]"}`}>
              Happy Customers
            </p>
          </div>
          </div>

          <div className="border-r-[#DADADA] py-6 pb-9  flex justify-center flex-col items-center">
            <div className='flex items-start flex-col justify-center'>
            <div className="p-0 m-0 font-bold mb-1 ">
              <span className="text-[#5081FF] text-[40px] font-bold leading-[150%] font-NeueMontreal text-start">
                <StatNumber display={`${Math.floor(projects)}+`} exact={PROJECTS_DELIVERED} />
              </span>
            </div>
            <p data-aos="zoom-in" className={`p-0 m-0 text-[18px] font-medium leading-[100%] schibsted-grotesk tracking-wide ${isFintech ? "text-white" : "text-[#fff]"}`}>
              Projects Delivered
            </p>
          </div>
          </div>
        </div>
        <UpdatedAt
          iso={STATS_UPDATED_AT}
          className={`mt-4 text-center text-[12px] font-normal schibsted-grotesk opacity-60 ${isFintech ? "text-white" : "text-[#fff]"}`}
        />
      </div>
    </section>
  );
};

export default StatsSection;